'use client';

import type { Address } from '@ahm/api-wrappers-core';
import { getCookie } from 'cookies-next';
import { useRouter } from 'next/navigation';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';

import Container from '../container';
import OrderLocationForm from '../order-location/order-location-form';
import { OrderLocationSheet } from '../order-location/order-location-sheet';
import { useGetStoreBySlug } from '@/api/use-get-store-by-slug';
import type { OrderStore } from '@/api/use-get-store-order';
import { convertStoreToOrderStore } from '@/api/utils';
import { ErudaLoader } from '@/components/eruda-loader';
import { ROUTES } from '@/config/router';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAiFoodOrderStore } from '@/store';
import { useCartStore } from '@/store/cart/cart-store-provider';

type WebstoreSessionState = {
  mode: 'webstore';
  accessToken: string | null;
  session: null;
  expireAt: null;
  isLoading: boolean;
  isLoadingStore: boolean;
  storeData?: OrderStore;
  locationSheetOpen: boolean;
  openLocationSheet: () => void;
  closeLocationSheet: () => void;
  toggleLocationSheet: () => void;
  sessionCountdownHidden: boolean;
  setSessionCountdownHidden: (hidden: boolean) => void;
};

type WebstoreSessionProviderProps = PropsWithChildren<{
  storeSlug?: string;
}>;

const WebstoreSessionContext = createContext<WebstoreSessionState>(
  {} as WebstoreSessionState
);

export const useWebstoreSession = () => {
  const context = useContext(WebstoreSessionContext);
  if (!context)
    throw new Error(
      'useWebstoreSession must be used within a WebstoreSessionProvider'
    );

  return context;
};

export const WebstoreSessionProvider = (
  props: WebstoreSessionProviderProps
) => {
  const { children, storeSlug: propStoreSlug } = props;

  // All hooks must be called at the top level
  const isMobile = useIsMobile();
  const router = useRouter();
  const aiFoodOrderAddress =
    useAiFoodOrderStore.use.address() as Address | null;
  const address = useAiFoodOrderStore.use.address();
  const setAddress = useCartStore((state) => state.setAddress);
  // const setStoreContext = useCartStore((state) => state.setStoreContext);

  // State hooks
  const [locationSheetOpen, setLocationSheetOpen] = useState(false);
  const [sessionCountdownHidden, setSessionCountdownHidden] = useState(false);

  const [storeSlug, setStoreSlug] = useState<string>(() => {
    if (propStoreSlug) return propStoreSlug;
    if (typeof window !== 'undefined') {
      return localStorage.getItem('ahafood.webstore_slug') ?? '';
    }
    return '';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = () => {
      const newSlug = localStorage.getItem('ahafood.webstore_slug') ?? '';
      if (newSlug !== storeSlug) {
        setStoreSlug(newSlug);
      }
    };

    // Kiểm tra thay đổi localStorage mỗi 100ms (để catch changes ngay lập tức)
    const interval = setInterval(handleStorageChange, 100);

    // Cũng lắng nghe storage event (cho cross-tab changes)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [storeSlug]);

  const token = (getCookie('ws_token') as string) ?? null;

  const { data: storeDataBySlug, isLoading: loading } = useGetStoreBySlug(
    storeSlug,
    { lat: address?.lat, lng: address?.lng },
    {
      enabled: !!storeSlug,
    }
  );

  const storeDataConverted = storeDataBySlug
    ? convertStoreToOrderStore(storeDataBySlug.store)
    : undefined;

  const openLocationSheet = useCallback(() => setLocationSheetOpen(true), []);

  const closeLocationSheet = useCallback(() => {
    router.replace(ROUTES.order);
    setLocationSheetOpen(false);
  }, [router]);

  const toggleLocationSheet = useCallback(
    () => setLocationSheetOpen((prev) => !prev),
    []
  );

  useEffect(() => {
    if (aiFoodOrderAddress) {
      setAddress(aiFoodOrderAddress);
    }
  }, [aiFoodOrderAddress, setAddress]);

  // Khi branchId hoặc storeId thay đổi ở webstore, sử dụng setStoreContext
  // DISABLED: Để tránh xung đột với logic trong use-store-detail.ts
  // useEffect(() => {
  //   const webstoreBranchId = storeDataConverted?.nearest_branch?.id ?? '';
  //   const webstoreStoreId = storeDataBySlug?.store?._id
  //     ? String(storeDataBySlug?.store?._id)
  //     : '';

  //   if (webstoreStoreId && webstoreBranchId) {
  //     setStoreContext({
  //       branchId: webstoreBranchId,
  //       storeId: webstoreStoreId,
  //       storeSlug: storeDataBySlug?.store?.slug,
  //       pageId: storeDataBySlug?.store?.page_id
  //         ? String(storeDataBySlug.store.page_id)
  //         : undefined,
  //     });
  //   }
  // }, [
  //   storeDataConverted?.nearest_branch?.id,
  //   storeDataBySlug?.store?._id,
  //   storeDataBySlug?.store?.slug,
  //   storeDataBySlug?.store?.page_id,
  //   setStoreContext,
  // ]);

  const value: WebstoreSessionState = {
    mode: 'webstore',
    accessToken: token,
    session: null,
    expireAt: null,
    isLoading: loading,
    isLoadingStore: false,
    storeData: storeDataConverted,
    locationSheetOpen,
    openLocationSheet,
    closeLocationSheet,
    toggleLocationSheet,
    sessionCountdownHidden,
    setSessionCountdownHidden,
  };

  return createElement(
    WebstoreSessionContext.Provider,
    { value },
    <>
      <WebstoreWithLocation>{children}</WebstoreWithLocation>
      <OrderLocationSheet
        open={locationSheetOpen}
        onOpenChange={toggleLocationSheet}
      />
      <ErudaLoader
        enabled={Boolean(isMobile && process.env.APP_ENV !== 'production')}
      />
    </>
  );
};

export const WebstoreWithLocation = ({ children }: PropsWithChildren) => {
  const address = useCartStore((state) => state.address);
  const { locationSheetOpen } = useWebstoreSession();

  // If location sheet is open, always render children
  if (locationSheetOpen) {
    return <>{children}</>;
  }

  if (!address?.lat || !address?.lng) {
    return (
      <Container>
        <OrderLocationForm
          handleClose={() => {
            // No action needed when used as fullscreen form
          }}
        />
      </Container>
    );
  }

  return <>{children}</>;
};
